import React from 'react';
import styled from 'styled-components';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RefreshIcon from '@material-ui/icons/Refresh';
import { CampaignAnalytics } from '../../../models';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { campaignChannelLabel, campaignGoalLabel, formatCampaignDateTime, getCampaignStatusTranslationKey } from '../campaignsUtils';
import { CampaignStatusBadge } from '../CampaignStatusBadge';
import AppButton from '../../../components/AppButton';

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
`;

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
    flex-wrap: wrap;
`;

const ActionsRow = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
    justify-content: space-between;
    flex-wrap: wrap;
`;

const CampaignName = styled.h2`
    margin: 0;
    font-size: 1.4rem;
    font-weight: 800;
    color: var(--text-body);
`;

const MetaRow = styled.div`
    display: flex;
    gap: var(--spacer-M);
    flex-wrap: wrap;
    color: var(--text-muted);
    font-size: 0.88rem;
`;

const MetaItem = styled.span`
    display: flex;
    align-items: center;
    gap: var(--spacer-XXS);
`;

interface CampaignAnalyticsHeaderProps {
    analytics: CampaignAnalytics;
    onBackToBuilder: () => void;
    onReTrigger: () => void;
    onRefresh: () => void;
    canReTrigger: boolean;
    reTriggerLoading: boolean;
    refreshLoading: boolean;
}

export const CampaignAnalyticsHeader: React.FC<CampaignAnalyticsHeaderProps> = ({
    analytics,
    onBackToBuilder,
    onReTrigger,
    onRefresh,
    canReTrigger,
    reTriggerLoading,
    refreshLoading,
}) => {
    const t = useTranslations();

    return (
        <Header>
            <ActionsRow>
                <AppButton color="link" size="sm" onClick={onBackToBuilder} style={{ padding: 0 }}>
                    <ArrowBackIcon style={{ fontSize: '1.1rem', verticalAlign: 'middle' }} />
                    {' '}{t(TK.campaignAnalyticsBackToBuilder)}
                </AppButton>
                <div style={{ display: 'flex', gap: 'var(--spacer-S)', alignItems: 'center' }}>
                    <AppButton color="link" size="sm" onClick={onRefresh} disabled={refreshLoading} style={{ padding: 0 }}>
                        <RefreshIcon style={{ fontSize: '1.2rem', verticalAlign: 'middle' }} />
                    </AppButton>
                    {canReTrigger && (
                        <AppButton
                            color="primary"
                            onClick={onReTrigger}
                            disabled={reTriggerLoading}
                            style={{ backgroundColor: 'var(--brand-primary)', borderColor: 'var(--brand-primary)' }}
                        >
                            {t(TK.campaignAnalyticsSendAgain)}
                        </AppButton>
                    )}
                </div>
            </ActionsRow>
            <TitleRow>
                <CampaignName>{analytics.campaignName}</CampaignName>
                <CampaignStatusBadge status={analytics.status} />
            </TitleRow>
            <MetaRow>
                <MetaItem>
                    <strong>{t(TK.channel)}:</strong>&nbsp;{t(campaignChannelLabel(analytics.channel))}
                </MetaItem>
                {analytics.goal != null && (
                    <MetaItem>
                        <strong>{t(TK.goal)}:</strong>&nbsp;{t(campaignGoalLabel(analytics.goal))}
                    </MetaItem>
                )}
                <MetaItem>
                    <strong>{t(TK.campaignLastTriggered)}:</strong>&nbsp;
                    {analytics.lastTriggeredAt ? formatCampaignDateTime(analytics.lastTriggeredAt) : t(TK.campaignNeverSent)}
                </MetaItem>
                {analytics.lastTriggeredBy && (
                    <MetaItem>
                        <strong>{t(TK.lastTriggeredBy)}:</strong>&nbsp;{analytics.lastTriggeredBy}
                    </MetaItem>
                )}
            </MetaRow>
        </Header>
    );
};
